import isEqual from 'lodash.isequal';
import * as React from 'react';
import { alphaSort, noop } from 'vet-bones/utils';
import { useDidValueChange } from 'vet-bones/utils/hooks';

import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  Mapping,
  MappingField,
} from 'src/containers/ProjectDiagram/types/data';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { addMappingField } from 'src/containers/ProjectDiagram/utils/mapping/addMappingField';
import { removeMappingField } from 'src/containers/ProjectDiagram/utils/mapping/removeMappingField';
import { useSuggestionContext } from 'src/containers/ProjectMapping/hooks/suggestion';
import { useMappingFromRoute } from 'src/containers/ProjectMapping/hooks/useMappingFromRoute';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { projectMappingAttributeIrisSelector } from 'src/selectors/projectMappingAttributes';
import { usePrevious } from 'src/utils/hooks/usePrevious';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type FieldValues = Array<string | null>;
export type FieldValuesByAttribute = Record<string, FieldValues>;

export type MappingFieldsByAttribute = Record<string, MappingField[]>;

export const buildFieldsByAttribute = (
  attributes: string[],
  fields: Record<string, MappingField>
) =>
  Object.values(fields).reduce<MappingFieldsByAttribute>(
    (acc, field) => {
      field.targets.forEach((attributeForField) => {
        if (!acc[attributeForField]) {
          acc[attributeForField] = [];
        }
        acc[attributeForField].push(field);
        acc[attributeForField].sort(alphaSort((field) => field.label));
      });
      return acc;
    },
    attributes.reduce<MappingFieldsByAttribute>(
      (agg, attribute) => ({
        ...agg,
        [attribute]: [],
      }),
      {}
    )
  );

export const buildFieldValuesByAttribute = (
  attributes: string[],
  fieldsByAttribute: MappingFieldsByAttribute
) => {
  const fieldValuesByAttribute: FieldValuesByAttribute = {};
  attributes.forEach((attribute) => {
    if (!fieldsByAttribute[attribute]?.length) {
      fieldValuesByAttribute[attribute] = [null];
    } else {
      fieldValuesByAttribute[attribute] = fieldsByAttribute[attribute].map(
        (field) => field.value
      );
    }
  });
  return fieldValuesByAttribute;
};

// context

export const AttributeFieldsContext = React.createContext<{
  fieldsByAttribute: MappingFieldsByAttribute;
  fieldValuesByAttribute: FieldValuesByAttribute;
  setFieldValuesByAttribute: (
    fieldValuesByAttribute: FieldValuesByAttribute
  ) => void;
}>({
  fieldsByAttribute: {},
  fieldValuesByAttribute: {},
  setFieldValuesByAttribute: noop,
});

export const useAttributeFieldsContext = () => {
  return React.useContext(AttributeFieldsContext);
};

export const useAttributeFieldsContextValue = () => {
  const { projectId } = useRouterParams();
  const mapping = useMappingFromRoute() as Mapping & { to: string };

  const { isLoading } = useSuggestionContext();
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));
  const attributes = useReduxSelector(
    projectMappingAttributeIrisSelector(projectId, mapping.value),
    isEqual
  );

  const fieldsByAttribute = React.useMemo(
    () => buildFieldsByAttribute(attributes, mapping.fields),
    [attributes, mapping]
  );

  const [fieldValuesByAttribute, setFieldValuesByAttribute] = React.useState(
    () => buildFieldValuesByAttribute(attributes, fieldsByAttribute)
  );

  const prevAttributes = usePrevious(attributes);

  const didIsDraftChange = useDidValueChange(isDraftDiagram);
  const didIsLoadingChange = useDidValueChange(isLoading);
  const needsReset = didIsDraftChange || (didIsLoadingChange && !isLoading);
  const didNeedsResetChange = useDidValueChange(needsReset);

  React.useEffect(() => {
    // reset fields after entering or leaving draft mode
    if (didNeedsResetChange) {
      setFieldValuesByAttribute(
        buildFieldValuesByAttribute(attributes, fieldsByAttribute)
      );
      return;
    }

    if (!prevAttributes) {
      return;
    }

    const removedAttributes = prevAttributes.filter(
      (prevAttribute) =>
        !attributes.some((attribute) => attribute === prevAttribute)
    );

    const addedAttributes = attributes.filter(
      (attribute) =>
        !prevAttributes.some((prevAttribute) => attribute === prevAttribute)
    );

    if (!removedAttributes.length && !addedAttributes.length) {
      return;
    }

    const updatedFieldValuesByAttribute = { ...fieldValuesByAttribute };
    if (removedAttributes.length) {
      removedAttributes.forEach((removedAttribute) => {
        delete updatedFieldValuesByAttribute[removedAttribute];
      });
    }

    if (addedAttributes.length) {
      addedAttributes.forEach((addedAttribute) => {
        const fields = fieldsByAttribute[addedAttribute];
        updatedFieldValuesByAttribute[addedAttribute] = fields.length
          ? fields.map((field) => field.value)
          : [null];
      });
    }

    setFieldValuesByAttribute(updatedFieldValuesByAttribute);
  }, [
    attributes,
    didNeedsResetChange,
    fieldsByAttribute,
    fieldValuesByAttribute,
    needsReset,
    prevAttributes,
  ]);

  return {
    fieldsByAttribute,
    fieldValuesByAttribute,
    setFieldValuesByAttribute,
  };
};

// handlers

export const useAddFieldValue = () => {
  const { fieldValuesByAttribute, setFieldValuesByAttribute } =
    useAttributeFieldsContext();

  return React.useCallback(
    ({ attributeIri, index }: { attributeIri: string; index: number }) => {
      const fieldValues = fieldValuesByAttribute[attributeIri];
      if (!fieldValues) {
        return;
      }

      const newFieldValuesByAttribute = { ...fieldValuesByAttribute };
      const newFieldValues = [...fieldValues];
      newFieldValues.splice(index, 0, null);
      newFieldValuesByAttribute[attributeIri] = newFieldValues;

      setFieldValuesByAttribute(newFieldValuesByAttribute);
    },
    [fieldValuesByAttribute, setFieldValuesByAttribute]
  );
};

export const useChangeFieldValue = () => {
  const diagram = useDiagram();
  const mapping = useMappingFromRoute() as Mapping & { to: string };
  const { key } = mapping;
  const { fieldValuesByAttribute, setFieldValuesByAttribute } =
    useAttributeFieldsContext();

  return React.useCallback(
    ({
      attributeIri,
      oldFieldId,
      newFieldId,
      index,
    }: {
      attributeIri: string;
      oldFieldId: string | null;
      newFieldId: string | null;
      index: number;
    }) => {
      if (diagram) {
        commitDiagramModel(diagram, (model) => {
          if (oldFieldId) {
            removeMappingField({
              attributeIri,
              fieldId: oldFieldId,
              key,
            })(model);
          }

          if (newFieldId) {
            addMappingField({
              attributeIri,
              fieldId: newFieldId,
              key,
            })(model);
          }
        });
      }

      const fieldValues = fieldValuesByAttribute[attributeIri];
      if (!fieldValues) {
        return;
      }

      const newFieldValuesByAttribute = { ...fieldValuesByAttribute };
      const newFieldValues = [...fieldValues];
      newFieldValues.splice(index, 1, newFieldId);
      newFieldValuesByAttribute[attributeIri] = newFieldValues;

      setFieldValuesByAttribute(newFieldValuesByAttribute);
    },
    [diagram, fieldValuesByAttribute, key, setFieldValuesByAttribute]
  );
};

export const useRemoveFieldValue = () => {
  const diagram = useDiagram();
  const mapping = useMappingFromRoute() as Mapping & { to: string };
  const { fieldValuesByAttribute, setFieldValuesByAttribute } =
    useAttributeFieldsContext();

  return React.useCallback(
    ({
      attributeIri,
      fieldId,
      index,
    }: {
      attributeIri: string;
      fieldId: string | null;
      index: number;
    }) => {
      if (diagram && fieldId) {
        commitDiagramModel(
          diagram,
          removeMappingField({
            attributeIri,
            fieldId,
            key: mapping.key,
          })
        );
      }

      const fieldValues = fieldValuesByAttribute[attributeIri];
      if (!fieldValues) {
        return;
      }

      const newFieldValuesByAttribute = { ...fieldValuesByAttribute };
      const newFieldValues = [...fieldValues];
      newFieldValues.splice(index, 1);
      newFieldValuesByAttribute[attributeIri] = newFieldValues;

      setFieldValuesByAttribute(newFieldValuesByAttribute);
    },
    [diagram, fieldValuesByAttribute, mapping, setFieldValuesByAttribute]
  );
};
